import React, { useState, useCallback, useMemo } from 'react';

import styled from '@emotion/styled';
import type { DataFlowHistogram } from '@rtkwlf/rendall';

import { useTheme } from '@packages/fenrir/styles';

import { SparkTooltip } from '../sparkline-tooltips/sparkline-tooltips';

const DEFAULT_HEIGHT = 48;
const DEFAULT_WIDTH = 200;
const X_OFFSET = 20;
const Y_OFFSET = 80;

const Cursor = styled.line<{ stroke: string }>(({ stroke }) => ({
  pointerEvents: 'none',
  stroke,
  strokeWidth: '0.5px',
}));

type Props = {
  data: Array<DataFlowHistogram>;
  width?: number;
  height?: number;
};

export const SparklineChart: React.FC<Props> = ({
  data,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
}) => {
  const theme = useTheme();

  const [activePoint, setActivePoint] = useState<DataFlowHistogram | null>(
    null,
  );
  const [xOffset, setXOffset] = useState<number>(0);
  const [yOffset, setYOffset] = useState<number>(0);
  const [tooltipCursor, setTooltipCursor] = useState<React.ReactNode | null>(
    null,
  );

  const { points, line, background } = useMemo(() => {
    if (!data?.length) {
      return { points: null, line: '', background: '' };
    }
    const max = Math.max(...(data ?? []).map((point) => point?.y || 0));
    const yScale = height / max;
    const xScale = width / data?.length;
    const points = data?.map((point, index) => ({
      x: (index + 1) * xScale,
      y: height - point.y * yScale - 1,
    }));

    if (!points?.length) {
      return { points: null, line: '', background: '' };
    }

    const line = points.map((point) => `${point.x},${point.y}`).join(' ');
    const background = `-.01,${height} ${line} ${points.at(-1)?.x ?? 0 + 0.01},${height}`;
    return { points, line, background };
  }, [data, height, width]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGElement>) => {
      if (!points || !points.length) {
        return;
      }
      const svgPixelBounds = event.currentTarget.getBoundingClientRect();
      const xPixel = Math.max(0, event.clientX - svgPixelBounds.left);
      const percentXOffset = Math.min(xPixel / svgPixelBounds.width, 1);
      const index = Math.round((points.length - 1) * percentXOffset);
      const pointX = points[index].x;

      if (data) {
        setActivePoint(data[index]);
      }
      setXOffset(event.clientX + X_OFFSET);
      setYOffset(event.clientY - Y_OFFSET);
      setTooltipCursor(
        <Cursor
          x1={pointX}
          x2={pointX}
          y1={0}
          y2={height}
          stroke={theme.palette.monochrome.mono3}
        />,
      );
    },
    [points, data, height, theme.palette.monochrome.mono3],
  );
  const handleMouseOut = useCallback(() => {
    setActivePoint(null);
    setTooltipCursor(null);
  }, []);

  return (
    <>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height="3rem"
        onMouseMove={handleMouseMove}
        onMouseOut={handleMouseOut}
        data-testid="sparkline-svg"
      >
        <defs>
          <linearGradient id="sparkline-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.palette.monochrome.mono2} />
            <stop offset="100%" stopColor={theme.palette.monochrome.mono4} />
          </linearGradient>
        </defs>
        <g>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            strokeWidth="1px"
            fill="transparent"
          />
          <polygon
            points={background}
            stroke="none"
            strokeWidth="0"
            fill="url(#sparkline-gradient)"
            data-testid="sparkline-fill"
          />
          {line && (
            <polyline
              points={line}
              strokeWidth="0.25px"
              fill="none"
              stroke={theme.palette.widget.widgetBackground}
              data-testid="sparkline-line"
            />
          )}
          {tooltipCursor}
        </g>
      </svg>
      <SparkTooltip point={activePoint} xOffset={xOffset} yOffset={yOffset} />
    </>
  );
};

export default SparklineChart;
